import type {
  AutoDealerLeaf,
  Brand,
  OrganizationLeaf,
} from "schema-dts";
import {
  dealerId,
  organizationId,
  parentOrganizationIdentity,
} from "../helpers/ids";
import { getUnitDisplayName } from "../../unit";
import type { SchemaBuilder, StructuredDataContext } from "../types";

function buildBrand(brandName: string | null): Brand | undefined {
  if (!brandName) return undefined;

  return { "@type": "Brand", name: brandName };
}

/**
 * Nome e URL são independentes: falta um, omite-se a propriedade, não o nó.
 * Só quando não há nenhum dos dois é que o grupo deixa de ser publicado.
 *
 * Sem URL o nó fica sem `@id` — o grupo é nomeado, mas deixa de ser a mesma
 * entidade entre os sites da conta, que é o ganho de informar a URL.
 */
function buildParentOrganization({
  organization,
}: StructuredDataContext): OrganizationLeaf | undefined {
  const { parentName, parentUrl } = organization;
  const identity = parentUrl ? parentOrganizationIdentity(parentUrl) : null;

  if (!parentName && !identity) return undefined;

  return {
    "@type": "Organization",
    "@id": identity?.id,
    name: parentName ?? undefined,
    url: identity?.url,
  };
}

/** Listagem resumida das unidades: identificador e nome, sem endereço nem geo. */
function buildSubOrganizations(
  context: StructuredDataContext,
): AutoDealerLeaf[] {
  return context.units.map((unit) => ({
    "@type": "AutoDealer",
    "@id": dealerId(unit.publicId || unit.id),
    name: getUnitDisplayName({ unit }),
    url: context.unitUrls?.[unit.id],
  }));
}

/**
 * `Organization` do site — o nó que aparece em toda página. Carrega a marca do
 * canal, as redes do site, o vínculo com o grupo controlador e a listagem
 * resumida das unidades.
 */
export const buildOrganization: SchemaBuilder = (context) => {
  const { baseUrl, channelPublicId, organization } = context;

  const node: OrganizationLeaf = {
    "@type": "Organization",
    "@id": organizationId(channelPublicId),
    name: organization.name ?? undefined,
    url: `${baseUrl}/`,
    logo: organization.logo ?? undefined,
    brand: buildBrand(organization.brandName),
    sameAs: organization.socialMedia.map((social) => social.url),
    parentOrganization: buildParentOrganization(context),
    subOrganization: buildSubOrganizations(context),
  };

  return [node];
};
